import { Component, DestroyRef, inject, OnInit, signal, WritableSignal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButton } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { ActivatedRoute } from "@angular/router";
import { KeycloakAuthService } from "@auth/services/keycloak-auth.service";
import { finalize, interval, map } from "rxjs";
import { BackendNotAvailableService } from "./backend-not-available.service";
import { TranslatePipe } from "@ngx-translate/core";
import { LanguageSelectorComponent } from "@shared/base/components/navigation/language-selector/language-selector.component";

const BLACK_LIST_ORIGIN: string[] = [
    "backend-not-available",
    "/backend-not-available",
];
const DEFAULT_ORIGIN = "/";
const RETRY_INTERVAL_SECONDS = 60;

@Component({
    selector: "app-backend-not-available",
    imports: [
        MatButton,
        MatIconModule,
        MatProgressSpinner,
        TranslatePipe,
        LanguageSelectorComponent,
    ],
    templateUrl: "./backend-not-available.component.html",
    styleUrl: "./backend-not-available.component.scss",
})
export default class BackendNotAvailableComponent implements OnInit {
    private readonly authService = inject(KeycloakAuthService);
    private readonly backendNotAvailableService = inject(BackendNotAvailableService);
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);

    private pathOrigin: string | null = null;
    private nextRetryAt: number = 0;

    public readonly retrying: WritableSignal<boolean> = signal<boolean>(false);
    public readonly retryFailed: WritableSignal<boolean> = signal<boolean>(false);
    public readonly secondsUntilRetry: WritableSignal<number> = signal<number>(RETRY_INTERVAL_SECONDS);

    ngOnInit(): void {
        this.pathOrigin = this.route.snapshot.queryParamMap.get("pathOrigin");
        this.scheduleNextRetry();
        this.startAutoRetryCountdown();
    }

    retryNow(): void {
        this.scheduleNextRetry();
        this.attemptReconnect(true);
    }

    logout(): void {
        this.authService.logOut();
    }

    private startAutoRetryCountdown(): void {
        interval(1000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.retrying()) {
                    return;
                }

                const remaining: number = Math.ceil((this.nextRetryAt - Date.now()) / 1000);
                if (remaining <= 0) {
                    this.scheduleNextRetry();
                    this.attemptReconnect(false);
                } else {
                    this.secondsUntilRetry.set(remaining);
                }
            });
    }

    private scheduleNextRetry(): void {
        this.nextRetryAt = Date.now() + RETRY_INTERVAL_SECONDS * 1000;
        this.secondsUntilRetry.set(RETRY_INTERVAL_SECONDS);
    }

    private attemptReconnect(manual: boolean): void {
        if (this.retrying()) {
            return;
        }

        this.retrying.set(true);
        this.retryFailed.set(false);
        this.backendNotAvailableService.testConnection().pipe(
            map((): string => this.resolveOrigin()),
            finalize((): void => this.retrying.set(false)),
        ).subscribe({
            next: (origin: string): void => this.redirectTo(origin),
            error: (): void => {
                if (manual) {
                    this.retryFailed.set(true);
                }
            },
        });
    }

    private resolveOrigin(): string {
        const origin: string = this.pathOrigin ?? DEFAULT_ORIGIN;
        if (BLACK_LIST_ORIGIN.includes(origin)) {
            return DEFAULT_ORIGIN;
        }
        return origin;
    }

    private redirectTo(origin: string): void {
        const protocol: string = location.protocol + "//";
        const host: string = location.host;
        const processedOrigin: string = origin.startsWith("/") ? origin : "/" + origin;
        location.replace(protocol + host + processedOrigin);
    }
}
